// Key-value storage on localStorage (ADR 0005). Falls back to memory when localStorage is
// unavailable or blocked (some private-browsing modes, disabled site data): the app then works
// for the session and forgets on reload, like a fresh visit.

export const PREFIX = 'kyc:v1:';

export class StorageFullError extends Error {
  override name = 'StorageFullError';
}

interface Backend {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  keys(): string[];
}

function memoryBackend(): Backend {
  const map = new Map<string, string>();
  return {
    getItem: (k) => map.get(k) ?? null,
    setItem: (k, v) => {
      map.set(k, v);
    },
    removeItem: (k) => {
      map.delete(k);
    },
    keys: () => [...map.keys()],
  };
}

function localBackend(storage: Storage): Backend {
  return {
    getItem: (k) => storage.getItem(k),
    setItem: (k, v) => {
      storage.setItem(k, v);
    },
    removeItem: (k) => {
      storage.removeItem(k);
    },
    keys: () =>
      Array.from({ length: storage.length }, (_, i) => storage.key(i)).filter(
        (k): k is string => k !== null,
      ),
  };
}

let backend: Backend | null = null;
let persistent = false;

function getBackend(): Backend {
  if (backend) return backend;
  try {
    const storage = globalThis.localStorage;
    const probe = `${PREFIX}probe`;
    storage.setItem(probe, '1');
    storage.removeItem(probe);
    backend = localBackend(storage);
    persistent = true;
  } catch {
    backend = memoryBackend();
    persistent = false;
  }
  return backend;
}

/** False when storage is blocked and the app only remembers things for this session. */
export function isPersistent(): boolean {
  getBackend();
  return persistent;
}

function isQuotaError(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED')
  );
}

/** Parsed JSON for `key`, or undefined when missing or corrupt. Callers know the shape they wrote. */
export function readJson(key: string): unknown {
  const raw = getBackend().getItem(PREFIX + key);
  if (raw === null) return undefined;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    // Corrupt entry (e.g. written by a broken build): treat as missing.
    return undefined;
  }
}

/** Throws StorageFullError when the browser's quota is reached. */
export function writeJson(key: string, value: unknown): void {
  try {
    getBackend().setItem(PREFIX + key, JSON.stringify(value));
  } catch (error) {
    if (isQuotaError(error)) throw new StorageFullError('Browser storage is full');
    throw error;
  }
}

export function remove(key: string): void {
  getBackend().removeItem(PREFIX + key);
}

/**
 * Removes everything this app stored except `keep` (ADR 0007): song lists, analyses and
 * per-song settings saved by earlier versions, which kept song data between visits.
 */
export function removeEntriesExcept(keep: string[]): void {
  const b = getBackend();
  const kept = new Set(keep.map((k) => PREFIX + k));
  for (const k of b.keys()) if (k.startsWith(PREFIX) && !kept.has(k)) b.removeItem(k);
}

/** Tests only: clear this app's entries and detect the backend again. */
export function resetStorageForTests(): void {
  const b = getBackend();
  for (const k of b.keys()) if (k.startsWith(PREFIX)) b.removeItem(k);
  backend = null;
}
